export const DESIGN_WIDTH = 1920
export const DESIGN_HEIGHT = 17203

export const INTRO_POSTER_VIDEO_SRC = 'https://cdn.storeel.com/website/video/1d861cb933f5408da003da2125f54cf1-3bb6d6daeb2f701db556b5b363d90218-hd-nbv2.mp4'
export const WORKFLOW_VIDEO_SRC = 'https://cdn.storeel.com/website/video/40f03cafbd8e4f03a0c27793f7c24b1e-7fafd8533a3ac4640f15f9a584e126c3-hd-nbv2.mp4'

export type Hotspot = {
  label: string
  x: number
  y: number
  w: number
  h: number
  href?: string
  external?: boolean
  scrollTo?: number
  scrollTarget?: string
  qrSrc?: string
  qrAlt?: string
  qrSize?: number
  qrOffset?: number
}

export type DesignBox = {
  x: number
  y: number
  w: number
  h: number
}

export type InteractionMask = DesignBox & {
  id: string
  radius?: number
}

export type FigmaSection = {
  id: string
  y: number
  h: number
  sourceH?: number
  cropTop?: number
  src: string
  srcset: string
  loading: 'eager' | 'lazy'
  fetchPriority: 'high' | 'auto'
}

export type ExpandCard = {
  title: string
  collapsedTitle?: string
  eyebrow?: string
  description: string
  details?: string[]
  image?: string
}

export type AboutStoryCard = {
  logo: string
  description: string
  textBox: DesignBox
}

export type HeroSlide = {
  title: string
  src: string
}

export type TextOverlayContent = {
  paragraphs: string[]
}

export type FaqItem = {
  question: string
  answer: string
}

export type CanvasFeatureCard = {
  title: string
  description: string
  image: string
  box: DesignBox
}

export type CanvasValueCard = {
  title: string
  description: string
  tone: 'blue' | 'image'
  box: DesignBox
}

export type PressCard = {
  name: string
  href: string
  image: string
  alt: string
  box: DesignBox
}

export type HeaderNavItem = {
  label: string
  x: number
  y: number
  w: number
  h: number
}

export type IntroStorySection = {
  box: DesignBox
  backgroundSrc: string
  videoSrc: string
  videoBox: DesignBox
  title: string
  titleBox: DesignBox
  description: string
  descriptionBox: DesignBox
}

export type WorkflowIntro = {
  title: string
  titleBox: DesignBox
  subtitle: string
  subtitleBox: DesignBox
}

const normalizedBase = (() => {
  const base = import.meta.env.BASE_URL || '/'
  return base.endsWith('/') ? base : `${base}/`
})()

const carouselBase = `${normalizedBase}home2026/carousels/`
const heroCarouselVersion = '20260701-home-banner'
const sectionImageVersion = '20260701-home-sections'
const canvasCommunityBase = `${normalizedBase}home2026/canvas-community/`
const expandCardsBase = `${normalizedBase}home2026/expand-cards/`
const pressBase = `${normalizedBase}home2026/press/`
const sectionBase = `${normalizedBase}home2026/sections/`
const iosQrSrc = new URL('../assets/img/web/ios.png', import.meta.url).href
const androidQrSrc = new URL('../assets/img/web/android.png', import.meta.url).href

const withSectionVersion = (src: string) => `${src}?v=${sectionImageVersion}`

const figmaSectionDefs = [
  { id: '01-hero', y: 0, h: 1085 },
  { id: '02-intro-video', y: 1085, h: 2239 },
  { id: '03-feature-carousel', y: 3324, h: 1080 },
  { id: '04-about', y: 4404, h: 1220 },
  { id: '05-create', y: 5374, h: 1810 },
  { id: '06-workflow', y: 7184, h: 1916 },
] as const

export const figmaSections: FigmaSection[] = figmaSectionDefs
  .map((section, index) => ({
    ...section,
    src: withSectionVersion(`${sectionBase}${section.id}.png`),
    srcset: [
      `${withSectionVersion(`${sectionBase}${section.id}-640.png`)} 640w`,
      `${withSectionVersion(`${sectionBase}${section.id}-960.png`)} 960w`,
      `${withSectionVersion(`${sectionBase}${section.id}.png`)} 1920w`,
    ].join(', '),
    loading: index === 0 ? 'eager' : 'lazy',
    fetchPriority: index === 0 ? 'high' : 'auto',
  }))

export const heroSlides: HeroSlide[] = [
  {
    title: "Dragon's Bond",
    src: `${carouselBase}hero-1.png?v=${heroCarouselVersion}`,
  },
  {
    title: "The Dragon King's Sacrificial Bride",
    src: `${carouselBase}hero-2.png?v=${heroCarouselVersion}`,
  },
  {
    title: 'The Witch Wife Living with a Muggle',
    src: `${carouselBase}hero-3.png?v=${heroCarouselVersion}`,
  },
  {
    title: 'Bride of the Underworld God',
    src: `${carouselBase}hero-4.png?v=${heroCarouselVersion}`,
  },
  {
    title: 'The King of Elves and the Mortal Maid',
    src: `${carouselBase}hero-5.png?v=${heroCarouselVersion}`,
  },
]

export const featureSlides = [1, 2, 3, 4, 5].map((index) => `${carouselBase}feature-${index}.png`)

export const headerActionItems: HeaderNavItem[] = [
  { label: 'StoReel Canvas', x: 1406, y: 40, w: 141, h: 40 },
  { label: 'Work With Us！', x: 1577, y: 40, w: 143, h: 40 },
]

export const introStorySection: IntroStorySection = {
  box: { x: 0, y: 1085, w: 1920, h: 1075 },
  backgroundSrc: withSectionVersion(`${sectionBase}intro-story-bg.jpg`),
  videoSrc: INTRO_POSTER_VIDEO_SRC,
  videoBox: { x: 300, y: 37, w: 563, h: 1000 },
  title: "Don't just watch the story. Step inside it.",
  titleBox: { x: 963, y: 741, w: 630, h: 140 },
  description:
    'Storeel turns vertical drama into an interactive story you can play. Choose your path, build bonds with characters, and shape the ending.',
  descriptionBox: { x: 963, y: 911, w: 673, h: 126 },
}
export const workflowVideoBox: DesignBox = { x: 0, y: 7520, w: 1920, h: 1080 }

export const aboutTitleBox: DesignBox = { x: 289, y: 4504, w: 1325, h: 70 }
export const aboutTitle = 'We’re building the world’ s next storytelling ecosystem.'
export const aboutCardsBox: DesignBox = { x: 200, y: 4674, w: 1520, h: 550 }
export const creatorTitleBox: DesignBox = { x: 187, y: 5424, w: 1545, h: 196 }
export const creatorTitle = 'StoReel empowers creators everywhere to turn imagination into impact.'
export const creatorCardsBox: DesignBox = { x: 200, y: 5670, w: 1520, h: 750 }
export const workflowIntro: WorkflowIntro = {
  title: 'How it works:',
  titleBox: { x: 393, y: 6520, w: 1134, h: 70 },
  subtitle: 'We Collaborate. You Create.',
  subtitleBox: { x: 393, y: 6640, w: 1134, h: 70 },
}
export const workflowCardsSlice: FigmaSection = {
  id: 'workflow-cards-overlay',
  y: 6760,
  h: 560,
  sourceH: 1916,
  cropTop: 76,
  src: withSectionVersion(`${sectionBase}06-workflow.png`),
  srcset: [
    `${withSectionVersion(`${sectionBase}06-workflow-640.png`)} 640w`,
    `${withSectionVersion(`${sectionBase}06-workflow-960.png`)} 960w`,
    `${withSectionVersion(`${sectionBase}06-workflow.png`)} 1920w`,
  ].join(', '),
  loading: 'lazy',
  fetchPriority: 'auto',
}
export const supportSystemBox: DesignBox = { x: 0, y: 12165, w: 1920, h: 2312 }

export const interactionMasks: InteractionMask[] = [
  { id: 'hero-carousel-mask', x: 0, y: 148, w: 1920, h: 760 },
  { id: 'about-clean-slate-mask', x: 0, y: 4404, w: 1920, h: 970 },
  { id: 'creator-workflow-clean-slate-mask', x: 0, y: 5374, w: 1920, h: 2146 },
  { id: 'support-system-mask', x: 0, y: 12165, w: 1920, h: 2312 },
]

export const aboutExpandedWidth = 900
export const aboutCollapsedWidth = 300
export const aboutGap = 10

export const creatorExpandedBox: DesignBox = { x: 0, y: 0, w: 1000, h: 750 }
export const creatorExpandedRightX = creatorCardsBox.w - creatorExpandedBox.w
export const creatorSmallX = 1053
export const creatorSmallBoxes: [DesignBox, DesignBox] = [
  { x: creatorSmallX, y: 0, w: 467, h: 350 },
  { x: creatorSmallX, y: 400, w: 467, h: 350 },
]

const aboutStoryLogo = `${expandCardsBase}storeel-logo-black.png`

export const aboutCards: AboutStoryCard[] = [
  {
    logo: aboutStoryLogo,
    description:
      'StoReel was born from a simple idea — stories should reflect more voices, travel farther, and connect deeper.',
    textBox: { x: 30, y: 321, w: 440, h: 168 },
  },
  {
    logo: aboutStoryLogo,
    description:
      'At StoReel, we bring independent creativity and AI-powered storytelling together to transform imagination into living worlds.',
    textBox: { x: 30, y: 290, w: 440, h: 210 },
  },
  {
    logo: aboutStoryLogo,
    description: 'We empower creators to build stories audiences can experience, shape, and return to.',
    textBox: { x: 29, y: 332, w: 440, h: 168 },
  },
]

export const creatorCards: ExpandCard[] = [
  {
    title: 'Create',
    description: 'Use ready-to-produce templates and creative assets to build your next hit faster.',
    image: `${expandCardsBase}create.png`,
  },
  {
    title: 'Monetize',
    description:
      'Access funding, co-production, and revenue-sharing opportunities with transparent data insights.',
    image: `${expandCardsBase}monetize.png`,
  },
  {
    title: 'Grow',
    description:
      'Reach global audiences through cross-platform marketing, algorithmic discovery, and localized distribution.',
    image: `${expandCardsBase}grow.png`,
  },
]

export const aboutCopyBox: DesignBox = { x: 982, y: 4522, w: 800, h: 520 }
export const aboutCopy: TextOverlayContent = {
  paragraphs: [
    'Built by filmmakers, technologists, and dreamers, StoReel is reimagining how creators and audiences experience storytelling. Our platform brings together the creative freedom of independent filmmaking with the reach and immediacy of digital culture — turning every story into a living, evolving world.',
    'From cinematic series to interactive experiences, StoReel empowers creators everywhere to produce, share, and grow stories that audiences can feel, follow, and shape. Together, we’re not just watching the future of entertainment — we’re making it.',
  ],
}

export const supportSystemCards = [
  {
    title: 'Data-Driven Insights',
    description: 'Real-time feedback and viewership analytics help you improve and scale.',
    tone: 'blue',
  },
  {
    title: 'Production Templates',
    description: 'Pre-tested scripts, AI-ready workflows, and creative frameworks for faster execution.',
    tone: 'lavender',
  },
  {
    title: 'Marketing Power',
    description: 'Cross-platform exposure on TikTok, Instagram, and YouTube.',
    tone: 'lavender',
  },
  {
    title: 'Community & Mentorship',
    description: 'Connect with other creators, exchange feedback, and grow together.',
    tone: 'blue',
  },
]

export const canvasCommunitySection = {
  height: 2815,
  logo: `${canvasCommunityBase}storeel-canvas-logo.png`,
  valueHoverImage: `${canvasCommunityBase}value-card-hover.png`,
  title: 'StoReel Canvas — The AI Storytelling Community',
  description:
    'Turn scripts, ideas, and characters into publishable stories with AI-powered agents. From storyboards to scenes to final videos, Canvas helps creators bring stories to life faster than ever.',
  creationTitle: 'All-in-One Creation.\nGlobal Distribution.\nSeamless Monetization.',
  systemTitle: '',
  creationDescription: 'A creator platform that transforms ideas into stories, audiences, and revenue.',
}

export const canvasFeatureCards: CanvasFeatureCard[] = [
  {
    title: 'One-Click Storyboards',
    description: 'Turn a single image into production-ready storyboards with multiple cinematic angles.',
    image: `${canvasCommunityBase}tool-storyboards.png`,
    box: { x: 200, y: 805, w: 500, h: 220 },
  },
  {
    title: 'More Efficient Production',
    description: 'Generate and export content in batches, built for scalable story creation.',
    image: `${canvasCommunityBase}tool-output.png`,
    box: { x: 710, y: 805, w: 500, h: 220 },
  },
  {
    title: 'Models Made Easy',
    description: 'Access leading AI image and video models from one unified workspace.',
    image: `${canvasCommunityBase}tool-models.png`,
    box: { x: 1220, y: 805, w: 500, h: 220 },
  },
  {
    title: 'Easier Monetization',
    description: 'Take on paid projects, publish your creations, and earn from your content.',
    image: `${canvasCommunityBase}tool-monetization.png`,
    box: { x: 200, y: 1035, w: 500, h: 220 },
  },
  {
    title: 'Creator Asset Library',
    description: 'Access thousands of creator-made templates, characters, scenes, and reusable assets.',
    image: `${canvasCommunityBase}tool-assets.png`,
    box: { x: 710, y: 1035, w: 500, h: 220 },
  },
  {
    title: 'One-Click Story Creation',
    description: 'Upload a script and let AI transform it into a complete story in minutes.',
    image: `${canvasCommunityBase}tool-drama.png`,
    box: { x: 1220, y: 1035, w: 500, h: 220 },
  },
]

export const canvasValueCards: CanvasValueCard[] = [
  {
    title: 'Unified Access\nto Leading AI\nModels',
    description: 'The best image and video generation models, integrated into a single workflow.',
    tone: 'blue',
    box: { x: 200, y: 2017, w: 372, h: 500 },
  },
  {
    title: 'Agent-Powered\nProduction Engine',
    description: 'Automatically break down scripts, generate storyboards, and produce content at scale.',
    tone: 'image',
    box: { x: 582, y: 2017, w: 372, h: 500 },
  },
  {
    title: 'Native Marketplace &\nMonetization',
    description: 'Connect creators with commercial opportunities, paid projects, and distribution channels.',
    tone: 'blue',
    box: { x: 965, y: 2017, w: 372, h: 500 },
  },
  {
    title: 'Production-Ready\nAsset Ecosystem',
    description:
      'Thousands of reusable characters, locations, scenes, and creator templates built for storytelling.',
    tone: 'blue',
    box: { x: 1348, y: 2017, w: 372, h: 500 },
  },
]

export const communityCopy = {
  title: 'A global creative community where stories, creators, and audiences grow together.',
  subtitle: '',
  description: '',
}

export const pressCards: PressCard[] = [
  {
    name: 'Business Insider',
    href: 'https://www.businessinsider.com/pitch-deck-storeel-raised-millions-build-ai-micro-dramas-2026-3',
    image: `${pressBase}business-insider-card.png`,
    alt: 'Business Insider coverage of StoReel',
    box: { x: 200, y: 608, w: 750, h: 365 },
  },
  {
    name: 'Variety',
    href: 'https://variety.com/2026/digital/news/storeel-canvas-ai-microdrama-platform-launch-1236787037/',
    image: `${pressBase}variety-card.png`,
    alt: 'Variety coverage of StoReel Canvas',
    box: { x: 970, y: 608, w: 750, h: 365 },
  },
  {
    name: 'Forbes',
    href: 'https://www.forbes.com/sites/ianshepherd/2026/04/02/how-ai-micro-dramas-and-interactivity-are-reshaping-entertainment/',
    image: `${pressBase}forbes-card.png`,
    alt: 'Forbes coverage of StoReel',
    box: { x: 200, y: 993, w: 750, h: 365 },
  },
  {
    name: 'TheWrap',
    href: 'https://www.thewrap.com/media-platforms/tv/ai-generated-microdramas-could-be-the-future-of-mobile-entertainment/',
    image: `${pressBase}thewrap-card.png`,
    alt: 'TheWrap coverage of AI-generated microdramas',
    box: { x: 970, y: 993, w: 750, h: 365 },
  },
]

export const faqBox: DesignBox = { x: 400, y: 15465, w: 1120, h: 997 }
export const faqIntro = {
  title: 'FAQ',
  subtitle: 'Answers for creators',
  description:
    'Explore the most common questions about interactive storytelling, AI-powered creation, and joining our global content community.',
}

export const faqItems: FaqItem[] = [
  {
    question: 'Who can become a StoReel creator?',
    answer:
      'StoReel works with filmmakers, writers, studios, and emerging AI creators who understand storytelling and want to explore new ways to create. If you have experience or passion in visual storytelling - whether through film, production, or creative writing - you can be part of our creator network.',
  },
  {
    question: 'Do I need to know how to use AI tools?',
    answer:
      'Not necessarily. Many of our creators come from traditional filmmaking backgrounds. StoReel provides creative support, tutorials, and access to our in-house AI resources - helping you blend cinematic craft with cutting-edge tools.',
  },
  {
    question: 'What kinds of stories work best on StoReel?',
    answer:
      'We focus on emotionally engaging, high-concept stories - often in genres like romance, mystery, fantasy, or thriller. Fast-paced, character-driven storytelling with strong emotional hooks.',
  },
  {
    question: 'How do creators earn on StoReel?',
    answer:
      'Creators can earn through co-production deals, revenue sharing, and brand collaborations. StoReel also helps top creators build long-term value through character-based IP that can evolve across multiple stories or formats.',
  },
  {
    question: 'Who owns the stories or characters I create?',
    answer:
      'You maintain ownership of your original stories and characters. StoReel helps you grow that IP, expand its world, and connect it with fans - so your work lives beyond a single project.',
  },
]

export const headerNavItems: HeaderNavItem[] = [
  { label: 'Home', x: 662, y: 40, w: 80, h: 40 },
  { label: 'About Us', x: 782, y: 40, w: 102, h: 40 },
  { label: 'Create', x: 924, y: 40, w: 84, h: 40 },
  { label: 'Contact', x: 1048, y: 40, w: 92, h: 40 },
  { label: 'News', x: 1180, y: 40, w: 78, h: 40 },
]

export const hotspots: Hotspot[] = [
  { ...headerNavItems[0], scrollTo: 0 },
  { ...headerNavItems[1], scrollTo: 4504 },
  { ...headerNavItems[2], scrollTo: 5424 },
  { ...headerNavItems[3], href: '#contact', scrollTarget: 'contact' },
  { ...headerNavItems[4], href: '#news', scrollTarget: 'news' },
  {
    label: 'StoReel Canvas',
    x: 1406,
    y: 40,
    w: 141,
    h: 40,
    href: 'https://canvas.storeel.com',
    external: true,
  },
  {
    label: 'Work With Us！',
    x: 1577,
    y: 40,
    w: 143,
    h: 40,
    href: 'mailto:support@storeel.com',
  },
  {
    label: 'Download on the App Store',
    x: 495,
    y: 920,
    w: 270,
    h: 84,
    qrSrc: iosQrSrc,
    qrAlt: 'App Store QR code',
    qrSize: 160,
    qrOffset: 12,
  },
  {
    label: 'Get it on Google Play',
    x: 825,
    y: 920,
    w: 270,
    h: 84,
    qrSrc: androidQrSrc,
    qrAlt: 'Google Play QR code',
    qrSize: 160,
    qrOffset: 12,
  },
  {
    label: 'Download APK',
    x: 1155,
    y: 920,
    w: 270,
    h: 84,
    href: 'https://apk.storeel.com/',
    external: true,
  },
  {
    label: 'Open StoReel Canvas',
    x: 843,
    y: 10655,
    w: 234,
    h: 48,
    href: 'https://canvas.storeel.com',
    external: true,
  },
  {
    label: 'Open StoReel Canvas again',
    x: 843,
    y: 11917,
    w: 234,
    h: 48,
    href: 'https://canvas.storeel.com',
    external: true,
  },
]
